import IcList from 'assets/Icons/IcList';
import Button from 'components/Button';
import Modal from 'components/Modal';
import appointmentSelectors from 'features/appointment/services/selectors';
import IcCheckService from 'features/ticketList/pages/TicketDetailsPage/components/ChangeServiceModal/IcCheckService';
import turnSelectors from 'features/turn/services/selectors';
import { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import HomeStaffCard from './HomeStaffCard';
import turnApis from 'features/turn/services/apis';
import dayjs from 'dayjs';
import { IStaffAvailableItem } from 'services/shop/types/staff';
import { IResponseDataBody } from 'services/response';
import ModalCallingStaff, {
  useModalCallingStaffRef,
} from 'features/user/pages/clockInClockOut/components/ModalCallingStaff';
import colorTheme from 'constants/color';

const ListServierTurnButton = () => {
  const [visible, setVisible] = useState(false);
  const [categorySelected, setCategorySelected] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const categoriesBase = appointmentSelectors.getCategories();
  const availableStaffs = turnSelectors.getTurnStaffsAvailable();
  const [staffsByService, setStaffsByService] = useState<IStaffAvailableItem[]>([]);
  const modalCallStaff = useModalCallingStaffRef();

  const handleOpenModal = () => {
    setVisible(true);
    setCategorySelected(categoriesBase[0]?.id || '');
  };
  const handleCloseModal = () => {
    setVisible(false);
    setSelectedServiceId('');
  };

  const servicesFilterByCategory = useMemo(() => {
    return categoriesBase.find((category) => category.id === categorySelected)?.services;
  }, [categoriesBase, categorySelected]);

  const handleClickService = (serviceId: string) => {
    if (selectedServiceId === serviceId) {
      setSelectedServiceId('');
    } else {
      setSelectedServiceId(serviceId);
    }
  };

  const fetchStaffsByService = async () => {
    try {
      if (selectedServiceId) {
        const res: IResponseDataBody<IStaffAvailableItem[]> = await turnApis.listStaffByService(
          [selectedServiceId],
          dayjs().format('MM-DD-YYYY'),
        );
        if (res?.data?.data) {
          setStaffsByService(res.data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching staffs by service:', error);
    }
  };

  useEffect(() => {
    fetchStaffsByService();
  }, [selectedServiceId]);

  const availableStaffsToDisplay = selectedServiceId ? staffsByService : availableStaffs;

  const onVoiceCall = (staff: IStaffAvailableItem) => modalCallStaff.current?.open(staff);

  return (
    <Container>
      <Button ntype='DEFAULT' newStyle iconElement={<IcList fill={colorTheme.gold_5} />} onClick={handleOpenModal}>
        Services
      </Button>
      <Modal
        visible={visible}
        modalTitle='Filter Staff By Service'
        containerPadding={0}
        v2
        width='85%'
        onClose={handleCloseModal}
        hiddenOkButton
        noneBodyStyle
      >
        <Content>
          <div>
            <CateGoryContainer>
              {categoriesBase.map((category) => (
                <CategoryItem
                  key={category.id}
                  selected={categorySelected === category.id}
                  onClick={() => setCategorySelected(category.id)}
                >
                  <div className='text'>{category.categoryName}</div>
                </CategoryItem>
              ))}
            </CateGoryContainer>
            <ServicesContainer>
              <ServicesContent>
                {servicesFilterByCategory?.map((service) => (
                  <ServiceItem
                    key={service.id}
                    selected={selectedServiceId.includes(service.id)}
                    onClick={() => handleClickService(service.id)}
                  >
                    {selectedServiceId.includes(service.id) && (
                      <div className='icon'>
                        <IcCheckService />
                      </div>
                    )}
                    <div className='name'>{service.serviceName}</div>
                    <div className='price'>{formatCurrency(service.priceSell)}</div>
                  </ServiceItem>
                ))}
              </ServicesContent>
            </ServicesContainer>
          </div>
          <StaffsContainer>
            <StaffsContent>
              {availableStaffsToDisplay.map((staff) => (
                <HomeStaffCard key={staff.staffId} data={staff} onVoiceCall={() => onVoiceCall(staff)} />
              ))}
            </StaffsContent>
          </StaffsContainer>
        </Content>
      </Modal>
      <ModalCallingStaff ref={modalCallStaff} />
    </Container>
  );
};

export default ListServierTurnButton;

const Container = styled.div`
  .DEFAULT {
    .CONTENT_2 {
      color: ${colorTheme.ink_7};
      font-size: 1.25rem;
    }
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 24px;
  padding: 36px 24px;
  font-family: 'Poppins';
`;

const CateGoryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  margin-bottom: 16px;
`;

const CategoryItem = styled.div<{ selected: boolean }>`
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  background: #fcfcfd;
  box-shadow:
    0 2px 4px 0 rgba(44, 35, 66, 0.4),
    0 4px 8px -3px rgba(45, 35, 66, 0.3),
    0 -3px 0 0 #d6d6e7 inset;
  cursor: pointer;
  min-width: 0;

  ${({ selected }) =>
    selected &&
    css`
      color: #fff;
      background: #2d6772;
      box-shadow:
        0 2px 4px 0 rgba(44, 35, 66, 0.4),
        0 4px 8px -3px rgba(45, 35, 66, 0.3),
        0 -3px 0 0 rgba(8, 56, 65, 0.5) inset;
    `}
  .text {
    font-size: 16px;
    font-weight: 600;
    line-height: 30px;
    text-transform: capitalize;
    color: #505050;
    min-width: 0;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    ${({ selected }) =>
      selected &&
      css`
        color: #fff;
      `}
  }
`;

const ServiceItem = styled.div<{ selected: boolean }>`
  cursor: pointer;
  height: 56px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-radius: 5px;
  background: #e5e6eb;
  box-sizing: border-box;
  border: 2px solid transparent;
  box-shadow:
    0 -4px 4px 0 rgba(0, 0, 0, 0.25) inset,
    0 8px 4px 3px rgba(255, 255, 255, 0.25) inset;
  transition: all 0.3s ease;
  min-width: 0;

  ${({ selected }) =>
    selected &&
    css`
      transition: all 0.3s ease;
      border-color: #d2464f;
    `}

  .name {
    flex: 1;
    overflow: hidden;
    color: #1d2129;
    text-overflow: ellipsis;
    font-size: 16px;
    font-weight: 400;
    line-height: normal;
    min-width: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .price {
    color: #1d2129;
    font-size: 16px;
    font-weight: 600;
    text-transform: capitalize;
  }
  .icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border-radius: 100px;
    background: #d2464f;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ServicesContainer = styled.div`
  height: 700px;
  overflow-y: auto;
`;

const ServicesContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 8px;
`;

const StaffsContainer = styled.div`
  height: 800px;
  overflow-y: auto;
`;

const StaffsContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 8px;
`;
